import { useRef, useState, useMemo } from 'react'
import {
  Modal,
  ModalProps,
  Text,
  Stack,
  Button,
  useToast,
  Box,
  Grid,
  Image,
  useHumpClubMutation,
} from '@thirstycamel/ui'
import { OfferEntity } from '@thirsty-camel/hump-club/src/modules/offer/offer.entity'
import { useAuthHeaders } from '../../hooks/useAuthHeaders'
import imageProxy from '../../utils/imageProxy'

export interface ModalSelectProductImageProps extends ModalProps {
  onDismiss?: () => void
  onConfirm?: (imageUrl: string, imageFilename: string) => void
  offer: OfferEntity
  modalName?: string
}

const ModalSelectProductImage: React.FC<ModalSelectProductImageProps> = ({
  isVisible,
  onDismiss,
  onConfirm,
  offer,
  modalName,
}) => {
  const toast = useToast()
  const authHeaders = useAuthHeaders()
  const [selectedImage, setSelectedImage] = useState<{
    url: string
    filename: string
    productId: string
  } | null>(null)

  const path = `offers/${offer?.id}/media/copy-product/${selectedImage?.productId ?? ''}`
  const [copyImage, mutation] = useHumpClubMutation<{ filename: string }, void>(
    [path, { method: 'post', ...authHeaders }],
    {
      onSuccess: data => {
        if (selectedImage) {
          toast({
            title: 'Image saved successfully',
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
          onConfirm?.(selectedImage.url, data.filename)
          onDismiss?.()
        }
      },
      onError: error => {
        toast({
          title: 'Failed to save image',
          description: error?.message ?? 'An error occurred',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      },
    },
  )

  // Collect all images from products attached to the offer
  const allImages = useMemo(() => {
    const images: Array<{
      url: string
      filename: string
      productName?: string
      productId: string
    }> = []

    // Use products from offer.productMeta if available
    const products = offer?.productMeta?.products || []

    products.forEach((product: any) => {
      if (product.media && product.media.length > 0 && product.id) {
        product.media.forEach(media => {
          if (media.filename) {
            images.push({
              url: imageProxy(media.filename, { height: 400 }),
              filename: media.filename,
              productName: product.name,
              productId: product.id,
            })
          }
        })
      }
    })

    return images
  }, [offer?.productMeta?.products])

  const handleConfirm = () => {
    if (!selectedImage || !offer?.id) {
      toast({
        title: 'Please select an image',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    copyImage()
  }

  const initialFocusRef = useRef(null)

  return (
    <Modal
      onClose={onDismiss}
      isVisible={isVisible}
      initialFocusRef={initialFocusRef}
      heading="Copy Image from Products"
      maxWidth={['lg', 'xl', '2xl', '4xl']}
    >
      <Stack py={2} spacing={5} flexShrink={0}>
        <Text fontSize="sm">
          Select an image to copy from products attached to this offer. All images from products
          with barcodes on this offer are shown below.
        </Text>

        {allImages.length === 0 ? (
          <Box p={5} textAlign="center">
            <Text color="gray.500">
              No product images found. Make sure products with barcodes on this offer have images
              attached.
            </Text>
          </Box>
        ) : (
          <Box maxH="60vh" overflowY="auto">
            <Grid
              gridTemplateColumns={['1fr', 'repeat(2, 1fr)', 'repeat(3, 1fr)', 'repeat(4, 1fr)']}
              gap={4}
            >
              {allImages.map((image, index) => (
                <Box
                  key={`${image.filename}-${index}`}
                  position="relative"
                  cursor="pointer"
                  borderWidth={2}
                  borderColor={selectedImage?.filename === image.filename ? 'pink.500' : 'gray.200'}
                  borderRadius="md"
                  p={2}
                  bg={selectedImage?.filename === image.filename ? 'pink.50' : 'white'}
                  onClick={() =>
                    setSelectedImage({
                      url: image.url,
                      filename: image.filename,
                      productId: image.productId,
                    })
                  }
                  transition="all 0.2s"
                >
                  <Image
                    src={image.url}
                    alt={image.productName || 'Product image'}
                    objectFit="contain"
                    height="200px"
                    width="100%"
                  />
                  {image.productName && (
                    <Text fontSize="xs" mt={2} textAlign="center" color="gray.600">
                      {image.productName}
                    </Text>
                  )}
                </Box>
              ))}
            </Grid>
          </Box>
        )}

        <Stack direction="row" spacing={3} justify="flex-end">
          <Button
            variant="outline"
            variantColor="pink"
            ref={initialFocusRef}
            isRound
            onClick={onDismiss}
            isDisabled={mutation.status === 'loading'}
          >
            Cancel
          </Button>

          <Button
            variantColor="pink"
            isRound
            onClick={handleConfirm}
            isLoading={mutation.status === 'loading'}
            isDisabled={!selectedImage || mutation.status === 'loading'}
          >
            Copy This Image
          </Button>
        </Stack>
      </Stack>
    </Modal>
  )
}

export default ModalSelectProductImage
